/**
 * @documental
 * @export Radio
 */

import { forwardRef } from "react";
import { withSubComponents } from "../../utilities/withSubComponents";
import { Field } from "./Field";
import { Input } from "./Input";
import { RefComponent } from "../RefComponent";
import { InputBaseProps } from "../InputBaseProps";
import { Placeholder } from "./Placeholder";
import { NoPaddingFormComponents } from "../shared";

/**
 * The `Radio` component properties.
 */

export interface RadioProps extends InputBaseProps<boolean | null>, NoPaddingFormComponents {}

/**
 * The Radio component sub components.
 */
export interface RadioSubComponents {
  /**
   * A React Hook Form compatible Radio component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the Radio which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * A form input that presents a radio button.
 *
 * `import { Radio } from "capitalrx-components-2.0/lib/forms";`
 *
 * <strong>To use the React Hook Form version add the following use the `Field` sub component:</strong>
 *
 * `<Radio.Field .../>`
 *
 * <strong>To use the regular version</strong>
 *
 * `<Radio .../>`
 *
 * The regular version acts like a normal field, in order to make it work
 * you need to pass in a `value` and an `onChange` for it to be interactive
 *
 * ```typescript
  const [value, onChange] = useSimpleInputState<boolean | null>(false);

  const handleChange = (newValue: boolean | null, prev: boolean | null) => {
    onChange(newValue, prev);
  };

  return <Radio {...props} label="Regular Radio Group" onChange={handleChange} value={value} />;
  ```
 *
 */
export const Radio = withSubComponents<RefComponent<HTMLInputElement, RadioProps>, RadioSubComponents>(
  {
    Field,
    Placeholder,
  },
  forwardRef<HTMLInputElement, RadioProps>(function Radio(props, ref) {
    return <Input {...props} ref={ref} />;
  }),
);
